import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useState } from "react";
import EnrollmentCertificateViewModal from "@/features/client-exams/components/EnrollmentCertificateViewModal";
import type {
  CertificateDto,
  EducationDetailDto,
  EnrollmentDto,
} from "../../types/client-education-detail.types";
import { EnrollmentStatus } from "../../types/client-education-detail.types";
import sectionStyles from "./sections.module.scss";

export function CertificateSection(props: {
  education: EducationDetailDto;
  enrollment: EnrollmentDto;
  certificate: CertificateDto | null;
}) {
  const { t } = useTranslation();
  const { education, enrollment, certificate } = props;
  const [openPreview, setOpenPreview] = useState(false);
  if (enrollment.status !== EnrollmentStatus.COMPLETED) return null;
  if (!education.certificateId) return null;

  if (!certificate) {
    return (
      <section id="certificate" className={sectionStyles.section}>
        <div className={sectionStyles.header}>
          <h2>{t("clientCertificateSection.title")}</h2>
        </div>
        <div className={sectionStyles.certificatePreparing}>
          <span className={sectionStyles.certificatePreparingIcon} aria-hidden>
            ⏳
          </span>
          <div className={sectionStyles.certificatePreparingText}>
            <strong>{t("clientCertificateSection.preparing")}</strong>
            <span>{t("clientCertificateSection.preparingHint")}</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="certificate" className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <h2>{t("clientCertificateSection.title")}</h2>
      </div>
      <div className={sectionStyles.certificateCard}>
        <div className={sectionStyles.certificateVisual} aria-hidden>
          <span className={sectionStyles.certificateIcon}>🏆</span>
        </div>
        <div className={sectionStyles.certificateInfo}>
          <span className={sectionStyles.certificateBadge}>
            ✓ {t("clientCertificateSection.issuedBadge")}
          </span>
          <div className={sectionStyles.certificateMetaList}>
            {certificate.certificateNumber && (
              <div className={sectionStyles.certificateMetaItem}>
                <span className={sectionStyles.certificateMetaLabel}>
                  {t("clientCertificateSection.documentNo")}
                </span>
                <span className={sectionStyles.certificateMetaValue}>
                  {certificate.certificateNumber}
                </span>
              </div>
            )}
            {certificate.issuedAt && (
              <div className={sectionStyles.certificateMetaItem}>
                <span className={sectionStyles.certificateMetaLabel}>
                  {t("clientCertificateSection.issuedAt")}
                </span>
                <span className={sectionStyles.certificateMetaValue}>
                  {dayjs(certificate.issuedAt).format("DD MMM YYYY")}
                </span>
              </div>
            )}
          </div>
          <div className={sectionStyles.certificateActions}>
            <button
              type="button"
              onClick={() => setOpenPreview(true)}
              className={sectionStyles.certActionSecondary}
            >
              👁 {t("clientCertificateSection.view")}
            </button>
            {certificate.downloadUrl && (
              <a
                href={certificate.downloadUrl}
                download
                className={sectionStyles.certActionPrimary}
              >
                ⬇ {t("clientCertificateSection.downloadPdf")}
              </a>
            )}
          </div>
        </div>
      </div>
      <EnrollmentCertificateViewModal
        educationId={education.id}
        enrollmentId={enrollment.id}
        isOpen={openPreview}
        onClose={() => setOpenPreview(false)}
      />
    </section>
  );
}
